import { present } from '@art-suite/art-core-ts';
import { useMemo } from 'react';

import {
  ApiOrgContractor,
  getOrgContractorQueryKey,
  getOrgContractorsByOrgMarketIdQueryKey,
  getOrgContractorsMap,
  getOrgContractorsQueryKey,
  getOrgContractorsQueryKeyPrefix,
  KaiProApiClient,
} from '@kaiizen/kai-pro-api';
import { useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';

import { ScanOfflineDataMinTtlOnAccessMs } from '../apiScanAndTemplateState/ScanStateLib';
import { useCreateUpdateMutatorsWithQueryCache } from '../reactQueryLib';
import { useKaiProApiClient } from '../useKaiProApiClient';

// Shared helper functions
const filterContractorsByTrades = (contractors: ApiOrgContractor[], trades?: string[]): ApiOrgContractor[] => {
  if (!trades || trades.length === 0) return contractors;
  return contractors.filter(contractor => contractor.trades && trades.some(trade => contractor.trades!.includes(trade)));
};

const useContractorMutators = (kaiProApiClient: KaiProApiClient) => {
  const queryCache = useQueryCache();

  return {
    ...useCreateUpdateMutatorsWithQueryCache({
      apiCreate: kaiProApiClient.createContractor.bind(kaiProApiClient),
      apiUpdate: kaiProApiClient.updateContractor.bind(kaiProApiClient),
      pluralKeyPrefix: getOrgContractorsQueryKeyPrefix(),
      getSingleKey: getOrgContractorQueryKey,
    }),
    addMarket: async (orgContractorId: string, orgMarketId: string) => {
      if (!kaiProApiClient.token) throw new Error('No token or orgId');
      const result = await kaiProApiClient.addMarketToContractor(orgContractorId, orgMarketId);
      queryCache.refetchAll({ prefix: getOrgContractorsQueryKeyPrefix() });
      return result;
    },
    removeMarket: async (orgContractorId: string, orgMarketId: string) => {
      if (!kaiProApiClient.token) throw new Error('No token or orgId');
      const result = await kaiProApiClient.removeMarketFromContractor(orgContractorId, orgMarketId);
      queryCache.refetchAll({ prefix: getOrgContractorsQueryKeyPrefix() });
      return result;
    },
  };
};

const useOrgContractorsBase = (queryKey: string, queryFn: () => Promise<ApiOrgContractor[]>, enabled: boolean, trades?: string[]) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    queryKey,
    async (key, queryCache) => {
      const contractors = await queryFn();
      contractors.forEach(contractor => {
        queryCache.cache.set(getOrgContractorQueryKey(contractor.id), contractor);
      });
      return contractors;
    },
    { enabled, minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs },
  );

  const [contractors, contractorsById] = useMemo(() => {
    const filteredContractors = query.data ? filterContractorsByTrades(query.data, trades) : undefined;
    return [filteredContractors, filteredContractors ? getOrgContractorsMap(filteredContractors) : undefined];
  }, [query.data, trades]);

  return {
    ...query,
    contractors,
    contractorsById,
    contractorsMap: contractorsById,
    contractorsMutators: useContractorMutators(kaiProApiClient),
  };
};

export const useOrgContractors = (options?: { trades?: string[]; archived?: boolean; search?: string }) => {
  const kaiProApiClient = useKaiProApiClient();
  return useOrgContractorsBase(
    getOrgContractorsQueryKey(options?.archived, options?.search),
    () => kaiProApiClient.getOrgContractors({ archived: options?.archived ? true : undefined, search: options?.search }),
    !!kaiProApiClient.token,
    options?.trades,
  );
};

export const useOrgContractorsByOrgMarketId = (orgMarketId?: string | null, trades?: string[]) => {
  const kaiProApiClient = useKaiProApiClient();
  const query = useOrgContractorsBase(
    getOrgContractorsByOrgMarketIdQueryKey(orgMarketId),
    () => kaiProApiClient.getContractorsByOrgMarketId(orgMarketId!),
    !!kaiProApiClient.token && present(orgMarketId),
    trades,
  );
  return {
    ...query,
    contractors: query.data,
  };
};

export const useOrgContractor = (id: string | null | undefined) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getOrgContractorQueryKey(id),
    () => kaiProApiClient.getContractor(id!),
    { enabled: !!kaiProApiClient.token && !!id },
  );

  return {
    ...query,
    contractorsMutators: useContractorMutators(kaiProApiClient),
    orgContractor: query.data,
  };
};
